import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import {
  FLAG_GETTING_REGISTERED_BUS_FLEETS,
  GET_REGISTERED_BUS_FLEETS,
  CLEAR_REGISTERED_BUS_FLEETS,
  GET_CURRENT_STATUS_REGISTERED_BUS_FLEETS,
  CLEAR_CURRENT_STATUS_REGISTERED_BUS_FLEETS,
  FLAG_GETTING_CURRENT_STATUS_REGISTERED_BUS_FLEETS,
  FLAG_BUS_RETENTION_SCHEDULE_ACTIVITY,
} from '../types';
import {
  BUS_RETENTION_SCHEDULE_ENDPOINT,
  generateBusRetentionScheduleEndpoint,
  generateLastBusRetentionScheduleByRegisteredBusEndpoint,
  REGISTERED_BUS_FLEET_ENDPOINT,
} from '../../config/endpoints';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
  DEFAULT_DELETE_CONFIG,
} from '../../config/rest';
import { tzNormalizeDate } from '../../utils/date';
import { DATE_TIME_LOCAL } from '../../config/locale';

const flagGettingRegisteredBusFleets = (flag) => (dispatch) =>
  dispatch({
    payload: flag,
    type: FLAG_GETTING_REGISTERED_BUS_FLEETS,
  });

const getRegisteredBusFleets = async (filters) => async (dispatch) => {
  try {
    dispatch(flagGettingRegisteredBusFleets(true));
    const url = `${REGISTERED_BUS_FLEET_ENDPOINT}/history?${QueryString.stringify(
      filters,
    )}`;
    const promise = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(promise, null, dispatch);
    const response = await promise.json();
    dispatch({
      type: GET_REGISTERED_BUS_FLEETS,
      payload: response,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingRegisteredBusFleets(false));
  }
};

const clearRegisteredBusFleets = () => (dispatch) =>
  dispatch({
    type: CLEAR_REGISTERED_BUS_FLEETS,
  });

const postBusRetentionSchedule = async (data) => async (dispatch) => {
  try {
    dispatch(flagGettingRegisteredBusFleets(true));
    const url = `${BUS_RETENTION_SCHEDULE_ENDPOINT}`;
    const promise = await fetch(url, {
      ...DEFAULT_POST_CONFIG,
      body: JSON.stringify(data),
    });
    await isErrorResponse(promise, null, dispatch);
    toastr.success(
      'Éxito',
      'El programa de retención de buses ha sido creado correctamente.',
    );
    return true;
  } catch ({ message }) {
    toastr.error('Error', message);
    return false;
  } finally {
    dispatch(flagGettingRegisteredBusFleets(false));
  }
};

const flagBusRetentionScheduleActivity = (flag) => (dispatch) =>
  dispatch({
    payload: flag,
    type: FLAG_BUS_RETENTION_SCHEDULE_ACTIVITY,
  });

const putBusRetentionSchedule =
  async (
    busRetentionScheduleId,
    { fromDateString, toDateString, locationId, registeredBusId },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagBusRetentionScheduleActivity(true));
      const payload = {
        id: busRetentionScheduleId,
        fromDateString,
        toDateString,
        locationId,
        registeredBusId,
      };
      const url = generateBusRetentionScheduleEndpoint(busRetentionScheduleId);
      const promise = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(promise, null, dispatch);
      toastr.success(
        'Éxito',
        'El programa de retención de buses ha sido actualizado correctamente.',
      );
      return true;
    } catch ({ message }) {
      toastr.error('Error', message);
      return false;
    } finally {
      dispatch(flagBusRetentionScheduleActivity(false));
    }
  };

const deleteBusRetentionSchedule =
  async ({ busRetentionScheduleId }) =>
  async (dispatch) => {
    try {
      dispatch(flagBusRetentionScheduleActivity(true));
      const url = generateBusRetentionScheduleEndpoint(busRetentionScheduleId);
      const promise = await fetch(url, DEFAULT_DELETE_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      toastr.success(
        'Éxito',
        'El programa de retención de buses ha sido eliminado correctamente.',
      );
      return true;
    } catch ({ message }) {
      toastr.error('Error', message);
      return false;
    } finally {
      dispatch(flagBusRetentionScheduleActivity(false));
    }
  };

const getLastBusRetentionScheduleByRegisteredBus =
  async (registeredBusId) => async (dispatch) => {
    try {
      const url =
        generateLastBusRetentionScheduleByRegisteredBusEndpoint(
          registeredBusId,
        );
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();
      return response;
    } catch ({ message }) {
      return null;
    }
  };

const flagGettingCurrentStatusRegisteredBusFleets = (flag) => (dispatch) =>
  dispatch({
    payload: flag,
    type: FLAG_GETTING_CURRENT_STATUS_REGISTERED_BUS_FLEETS,
  });

const clearCurrentStatusRegisteredBusFleets = () => (dispatch) =>
  dispatch({
    type: CLEAR_CURRENT_STATUS_REGISTERED_BUS_FLEETS,
  });

const getCurrentStatusRegisteredBusFleets =
  async ({ baseLocationId, serviceTypeIds, showNorthChico, showMine }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingCurrentStatusRegisteredBusFleets(true));
      const url = `${REGISTERED_BUS_FLEET_ENDPOINT}/current-status?${QueryString.stringify(
        { baseLocationId, serviceTypeIds, showNorthChico, showMine },
      )}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();
      dispatch({
        type: GET_CURRENT_STATUS_REGISTERED_BUS_FLEETS,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingCurrentStatusRegisteredBusFleets(false));
    }
  };

const exportRegisteredBusFleets = async (filters) => async (dispatch) => {
  try {
    dispatch(flagGettingRegisteredBusFleets(true));
    const url = `${REGISTERED_BUS_FLEET_ENDPOINT}/export-history-extended?${QueryString.stringify(
      filters,
    )}`;
    const promise = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(promise);
    const blob = await promise.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const now = tzNormalizeDate({ format: DATE_TIME_LOCAL });
    link.href = downloadUrl;
    link.download = `flota-registrada-${now}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingRegisteredBusFleets(false));
  }
};

export {
  getRegisteredBusFleets,
  clearRegisteredBusFleets,
  postBusRetentionSchedule,
  putBusRetentionSchedule,
  deleteBusRetentionSchedule,
  getLastBusRetentionScheduleByRegisteredBus,
  getCurrentStatusRegisteredBusFleets,
  clearCurrentStatusRegisteredBusFleets,
  exportRegisteredBusFleets,
};
